import React, { useState, useEffect } from 'react';
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import { VoteGroupService, ElectionService } from '../services/firebaseService';
import { VoteGroup, Election } from '../types';

interface CreateVoteGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateVoteGroupModal: React.FC<CreateVoteGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [elections, setElections] = useState<Election[]>([]);
  
  // Données du groupe
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedElections, setSelectedElections] = useState<string[]>([]);

  // Charger les élections disponibles
  useEffect(() => {
    if (isOpen) {
      const loadElections = async () => {
        try {
          const electionsData = await ElectionService.getAllElections();
          setElections(electionsData);
        } catch (error) {
          console.error('Erreur lors du chargement des élections:', error);
        }
      };
      loadElections();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Valider les données
      if (!name || !startDate || !endDate) {
        throw new Error('Tous les champs obligatoires doivent être remplis');
      }

      if (selectedElections.length === 0) {
        throw new Error('Au moins une élection doit être sélectionnée');
      }

      if (new Date(startDate) >= new Date(endDate)) {
        throw new Error('La date de fin doit être après la date de début');
      }

      // Créer le groupe d'élections
      const voteGroup: Omit<VoteGroup, 'id'> = {
        name,
        description,
        elections: selectedElections,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        isActive: true,
        emailSent: false
      };

      await VoteGroupService.createVoteGroup(voteGroup);
      
      // Réinitialiser le formulaire
      setName('');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setSelectedElections([]);
      
      onSuccess();
      onClose();

    } catch (error: any) {
      setError(error.message || 'Erreur lors de la création du groupe');
    } finally {
      setLoading(false);
    }
  };

  const toggleElection = (electionId: string) => {
    setSelectedElections(prev => 
      prev.includes(electionId) 
        ? prev.filter(id => id !== electionId)
        : [...prev, electionId]
    );
  };

  const moveElection = (fromIndex: number, toIndex: number) => {
    const newElections = [...selectedElections];
    const [movedElection] = newElections.splice(fromIndex, 1);
    newElections.splice(toIndex, 0, movedElection);
    setSelectedElections(newElections);
  };

  const removeElection = (electionId: string) => {
    setSelectedElections(prev => prev.filter(id => id !== electionId));
  };

  const getSelectedElectionsDetails = () => {
    return selectedElections.map(id => elections.find(e => e.id === id)).filter(Boolean) as Election[];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Créer un groupe d'élections</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Informations de base */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom du groupe *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input-field"
                placeholder="Ex: Élections Exécutives 2025"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input-field"
                placeholder="Description du groupe d'élections"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début *
              </label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin *
              </label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input-field"
                required
              />
            </div>
          </div>

          {/* Sélection des élections */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Élections à inclure dans le groupe
            </label>

            {/* Élections disponibles */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Élections disponibles</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {elections.map((election) => (
                  <div key={election.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="checkbox"
                      id={`election-${election.id}`}
                      checked={selectedElections.includes(election.id)}
                      onChange={() => toggleElection(election.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor={`election-${election.id}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                        {election.title}
                      </label>
                      <p className="text-xs text-gray-500">{election.position}</p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {election.candidates.length} candidat(s)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Ordre des élections sélectionnées */}
            {selectedElections.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Ordre de vote ({selectedElections.length} élection(s))
                </h4>
                <div className="space-y-2">
                  {getSelectedElectionsDetails().map((election, index) => (
                    <div key={election.id} className="flex items-center space-x-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{election.title}</p>
                        <p className="text-xs text-gray-500">{election.position}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {index > 0 && (
                          <button
                            type="button"
                            onClick={() => moveElection(index, index - 1)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Déplacer vers le haut"
                          >
                            ↑
                          </button>
                        )}
                        {index < selectedElections.length - 1 && (
                          <button
                            type="button"
                            onClick={() => moveElection(index, index + 1)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Déplacer vers le bas"
                          >
                            ↓
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeElection(election.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Retirer du groupe"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || selectedElections.length === 0}
              className="btn-primary"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : null}
              {loading ? 'Création...' : 'Créer le groupe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVoteGroupModal;
